import { useEffect, useRef, type PropsWithChildren, type ReactNode } from "react";

interface ModalProps extends PropsWithChildren {
  open: boolean;
  onClose: () => void;
  title: string;
  // One line under the title saying what the dialog acts on.
  subtitle?: ReactNode;
  // Rendered in the footer; the caller owns the submit button.
  footer?: ReactNode;
}

// Built on <dialog showModal()>, so the browser supplies the focus trap, the
// top layer, Escape and inertness of the page behind. Only the backdrop click
// and the scroll lock are ours.
export function Modal({ open, onClose, title, subtitle, footer, children }: ModalProps) {
  const dialog = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const element = dialog.current;
    if (!element) return undefined;
    if (open && !element.open) {
      element.showModal();
      document.body.style.overflow = "hidden";
    }
    if (!open && element.open) element.close();
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  if (!open) return null;

  return (
    <dialog
      className="modal" ref={dialog} aria-labelledby="modal-title"
      onCancel={(event) => { event.preventDefault(); onClose(); }}
      onClick={(event) => {
        // A click that lands on the dialog element itself is on the backdrop:
        // the panel inside covers the whole content box.
        if (event.target === dialog.current) onClose();
      }}
    >
      <div className="modal-panel" role="document">
        <div className="modal-head">
          <div>
            <h2 id="modal-title">{title}</h2>
            {subtitle ? <p className="modal-subtitle">{subtitle}</p> : null}
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="Close" title="Close">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer ? <div className="modal-foot">{footer}</div> : null}
      </div>
    </dialog>
  );
}
